import classnames from 'classnames';
import React, { useRef, useState } from 'react';
import {
    getFramework,
    getOperateDirection,
    getOperateType,
    MoveListenerLevel,
    useIdentity,
    useMoveEventListener,
    useScale,
} from '@/framework';
import { useScaleCursor } from '@/utils/cursor-kit';
import { ITouchKitEvent } from '@/utils/event-kit';
import {
    BaseLayerViewType,
    HIDDEN_OPERATE_MIN_HEIGHT,
    HIDDEN_OPERATE_MIN_WIDTH,
    ILayerProps,
    ScaleOperateType,
} from '@/version-types';
import { supportUnequalStretch } from '../../utils';
import { useScaleOperate } from '../hooks/use-scale-operate';

export const ScaleOperate: React.FC<{ layer: ILayerProps }> = ({ layer }) => {
    const identity = useIdentity();
    const framework = getFramework(identity);
    const [curOperateType, setCurOperateType] = useState<ScaleOperateType | null>(null);
    const { hiddenBtns } = framework.service.behavior.get(layer)?.scaleOperateStyle?.() ?? {};
    const scaleOperate = useScaleOperate();
    const { width, height, rotate } = layer;
    const cursor = useScaleCursor(rotate);

    const scaleInfo = useRef<{
        id: string;
        direction: ScaleOperateType;
        operateType: BaseLayerViewType;
        cursor: string;
    } | null>(null);

    function checkCanScale(e: ITouchKitEvent) {
        const target = e.target;
        if (!target) return;
        const operateType = getOperateType(e.target);
        if (operateType !== BaseLayerViewType.SCALE) {
            return;
        }
        const direction = getOperateDirection(e.target);
        const cursorKey = direction?.toUpperCase();
        return { id: layer.id, direction, cursor: cursor[cursorKey], operateType };
    }

    useMoveEventListener(
        (e) => {
            // if (framework.operate.isDisableScale()) return;
            setCurOperateType(
                scaleInfo.current?.operateType === BaseLayerViewType.SCALE
                    ? scaleInfo.current?.direction
                    : (BaseLayerViewType.MOVE as any),
            );
            if (e.isEnd) {
                setCurOperateType(null);
                if (scaleInfo.current) {
                    document.body.classList.remove(scaleInfo.current.cursor, scaleInfo.current.direction);
                }
            }
            if (!scaleInfo.current) {
                const res = checkCanScale(e);
                if (!res) return;
                scaleInfo.current = res;
            }
            if (e.isStart && scaleInfo.current) {
                scaleOperate.beforeScale(scaleInfo.current.id, scaleInfo.current.direction);
                if (scaleInfo.current.operateType === BaseLayerViewType.SCALE) {
                    document.body.classList.add(scaleInfo.current.cursor, scaleInfo.current.direction);
                }
            }
        },
        MoveListenerLevel.PREPARE_LEVEL,
        'scale-pre',
    );

    useMoveEventListener(
        (e: ITouchKitEvent) => {
            // if (framework.operate.isDisableScale()) return;
            if (!scaleInfo.current) return;
            const isUnequalStretch = supportUnequalStretch(identity, layer);
            scaleOperate.scale(scaleInfo.current.id, rotate, e, !isUnequalStretch);
            if (e.isEnd) {
                scaleInfo.current = null;
            }
        },
        MoveListenerLevel.DEFAULT_LEVEL,
        'scale-default',
    );
    const scale = useScale();
    const scaleWidth = width * scale;
    const scaleHeight = height * scale;
    const minHide = scaleWidth <= HIDDEN_OPERATE_MIN_WIDTH || scaleHeight <= HIDDEN_OPERATE_MIN_HEIGHT;

    // const isDisableScale = useIsDisableScale();
    // if (isDisableScale) return null;

    return (
        <div
            className={classnames('active-operate__scale', {
                mini: minHide,
            })}
        >
            {!hiddenBtns?.includes(ScaleOperateType.LT) && (
                <span
                    className={classnames('vertex', ScaleOperateType.LT, cursor.LT, {
                        'is-none': minHide && curOperateType !== ScaleOperateType.LT,
                        'is-hidden': curOperateType && curOperateType !== ScaleOperateType.LT,
                    })}
                    data-operate-type={BaseLayerViewType.SCALE}
                    data-direction={ScaleOperateType.LT}
                ></span>
            )}
            {!hiddenBtns?.includes(ScaleOperateType.RB) && (
                <span
                    className={classnames('vertex', ScaleOperateType.RB, cursor.RB, {
                        'is-hidden': curOperateType && curOperateType !== ScaleOperateType.RB,
                    })}
                    data-operate-type={BaseLayerViewType.SCALE}
                    data-direction={ScaleOperateType.RB}
                ></span>
            )}
            {!hiddenBtns?.includes(ScaleOperateType.LB) && (
                <span
                    className={classnames('vertex', ScaleOperateType.LB, cursor.LB, {
                        'is-none': minHide && curOperateType !== ScaleOperateType.LB,
                        'is-hidden': curOperateType && curOperateType !== ScaleOperateType.LB,
                    })}
                    data-operate-type={BaseLayerViewType.SCALE}
                    data-direction={ScaleOperateType.LB}
                ></span>
            )}
            {!hiddenBtns?.includes(ScaleOperateType.RT) && (
                <span
                    className={classnames('vertex', ScaleOperateType.RT, cursor.RT, {
                        'is-none': minHide && curOperateType !== ScaleOperateType.RT,
                        'is-hidden': curOperateType && curOperateType !== ScaleOperateType.RT,
                    })}
                    data-operate-type={BaseLayerViewType.SCALE}
                    data-direction={ScaleOperateType.RT}
                ></span>
            )}
        </div>
    );
};
